import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Box, Checkbox, Group, Stack, Text } from "@mantine/core";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { IconGripVertical } from "@tabler/icons-react";
import { useResetSetting, useUpdateSetting } from "../api/admin";
import { ApiError } from "../api/client";
import type { Setting } from "../api/types";
import { Chip } from "../ui/Chip";
import { alpha, token } from "../ui/tokens";
import { FeedbackLine } from "../ui/FeedbackLine";

/** One provider: rank, label, handle. */
const PROVIDER_ROW: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "24px minmax(0, 1fr) auto",
  gap: 10,
  alignItems: "center",
  padding: "8px 10px",
  border: "1px solid var(--mantine-color-default-border)",
  borderRadius: "var(--mantine-radius-sm)",
  background: alpha(token.black, 0.04),
};

const LABEL_CELL: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  minWidth: 0,
  cursor: "pointer",
};

/** Only the grip drags, so the checkbox and its blurb stay clickable and selectable. */
const HANDLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  cursor: "grab",
  color: "var(--mantine-color-dimmed)",
};

/**
 * The cover art provider chain: which catalogues to ask, and in what order.
 *
 * A stacked list rather than a set of checkboxes plus a separate order field, because the
 * two facts are one decision. Position is priority — the first enabled provider that has
 * the record wins and the rest are never asked — so the list has to show the order it will
 * actually be tried in, disabled entries included.
 *
 * Order is changed by dragging the grip, which is also how it is changed from the keyboard:
 * focus the grip, space to lift, arrows to move, space to drop.
 */

// `label` is a proper noun and stays literal; the blurb is copy, so the table holds its
// catalogue key and the render site translates it.
const KNOWN: Record<string, { label: string; blurbKey: string }> = {
  itunes: { label: "iTunes", blurbKey: "artworkProviders.blurbItunes" },
  deezer: { label: "Deezer", blurbKey: "artworkProviders.blurbDeezer" },
  musicbrainz: {
    label: "MusicBrainz + Cover Art Archive",
    blurbKey: "artworkProviders.blurbMusicbrainz",
  },
};

export interface ProviderRow {
  name: string;
  enabled: boolean;
}

export function ArtworkProviders({ setting }: { setting: Setting }) {
  const { t } = useTranslation("admin");
  const update = useUpdateSetting();
  const reset = useResetSetting();
  // The server is the authority on this value: it fills in providers a stored list is
  // missing, so what comes back from a save is not always what was sent. An in-flight edit
  // is therefore held as an override tagged with the server value it was applied on top of,
  // and is dropped the moment a fresh one arrives.
  const [pending, setPending] = useState<{ base: unknown; rows: ProviderRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rows = pending && pending.base === setting.value ? pending.rows : parseRows(setting.value);

  const save = async (next: ProviderRow[]) => {
    const base = setting.value;
    const previous = rows;
    setPending({ base, rows: next }); // optimistic: reordering should feel like moving a thing, not like a form
    setError(null);
    try {
      await update.mutateAsync({ key: setting.key, value: next });
    } catch (caught) {
      setPending({ base, rows: previous });
      setError(caught instanceof ApiError ? caught.detail : String(caught));
    }
  };

  const move = (from: number, to: number) => {
    const next = moveRow(rows, from, to);
    if (next !== rows) void save(next);
  };

  const toggle = (index: number) =>
    void save(rows.map((row, i) => (i === index ? { ...row, enabled: !row.enabled } : row)));

  const active = rows.filter((row) => row.enabled);

  // No rule and no padding of its own: `SettingShell` draws the row this sits inside.
  return (
    <>
      {/* A drag cancelled outside the list has no destination, and is not a reorder. */}
      <DragDropContext
        onDragEnd={({ source, destination }) => {
          if (destination) move(source.index, destination.index);
        }}
      >
        <Droppable droppableId="artwork-providers">
          {(dropzone) => (
            <Stack
              component="ol"
              gap={4}
              style={{ listStyle: "none", margin: 0, padding: 0 }}
              ref={dropzone.innerRef}
              {...dropzone.droppableProps}
            >
              {rows.map((row, index) => {
                const meta = KNOWN[row.name];
                const label = meta?.label ?? row.name;
                const position = active.indexOf(row) + 1;
                return (
                  <Draggable key={row.name} draggableId={row.name} index={index}>
                    {(drag, snapshot) => (
                      <Box
                        component="li"
                        ref={drag.innerRef}
                        {...drag.draggableProps}
                        style={{
                          ...PROVIDER_ROW,
                          // Dimmed, not hidden: a disabled provider keeps its place in the list.
                          ...(row.enabled ? {} : { opacity: 0.5 }),
                          ...(snapshot.isDragging
                            ? {
                                opacity: 1,
                                borderColor: "var(--mantine-color-accent-filled)",
                                background: "var(--mantine-color-body)",
                                boxShadow: "var(--mantine-shadow-md)",
                              }
                            : {}),
                          ...drag.draggableProps.style,
                        }}
                      >
                        {/* Position is priority, so it is numbered rather than merely ordered. */}
                        <Text
                          component="span"
                          size="xs"
                          c="dimmed"
                          ta="center"
                          aria-hidden="true"
                          style={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {row.enabled ? position : "—"}
                        </Text>

                        <Checkbox
                          style={LABEL_CELL}
                          checked={row.enabled}
                          onChange={() => toggle(index)}
                          label={label}
                          description={meta ? t(meta.blurbKey) : row.name}
                        />

                        <Box
                          component="span"
                          style={HANDLE}
                          aria-label={t("artworkProviders.dragHandle", { provider: label })}
                          {...drag.dragHandleProps}
                        >
                          <IconGripVertical size={16} stroke={1.5} aria-hidden="true" />
                        </Box>
                      </Box>
                    )}
                  </Draggable>
                );
              })}
              {dropzone.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>

      <Group gap={10} mt={8} fz={12} c="dimmed">
        {/* Not an error state. A deployment with no outbound route may well want this, and
            it is not the same thing as a misconfiguration — but it does mean every track
            ends up with a generated gradient, which is worth saying out loud. */}
        {active.length === 0
          ? t("artworkProviders.summaryNone")
          : t("artworkProviders.summaryActive", {
              providers: active.map((row) => KNOWN[row.name]?.label ?? row.name).join(" → "),
            })}
        {setting.source === "db" && (
          <Chip onClick={() => void reset.mutateAsync(setting.key)}>
            {t("artworkProviders.reset")}
          </Chip>
        )}
      </Group>

      <FeedbackLine role="alert">{error}</FeedbackLine>
    </>
  );
}

/**
 * Move a row, or return the list unchanged when the move would go nowhere.
 *
 * Identity is the signal: the caller saves only when something moved, so a drop back onto
 * the same row does not cost a write and an audit entry.
 */
export function moveRow(rows: ProviderRow[], from: number, to: number): ProviderRow[] {
  if (from === to || from < 0 || from >= rows.length || to < 0 || to >= rows.length) {
    return rows;
  }
  const next = rows.slice();
  const [row] = next.splice(from, 1);
  next.splice(to, 0, row!);
  return next;
}

/** Read the stored value, tolerating the older bare-string form and anything unexpected. */
export function parseRows(value: unknown): ProviderRow[] {
  if (!Array.isArray(value)) return [];
  const rows: ProviderRow[] = [];
  for (const entry of value) {
    if (typeof entry === "string") rows.push({ name: entry, enabled: true });
    else if (entry && typeof entry === "object" && "name" in entry) {
      const row = entry as { name: unknown; enabled?: unknown };
      if (typeof row.name === "string") {
        rows.push({ name: row.name, enabled: row.enabled !== false });
      }
    }
  }
  return rows;
}
